/*
 * Copyright © 2021 Lexcelon LLC <info@lexcelon.com>
 * Licensed for non-distributable use
 */
import PropTypes from 'prop-types';
import React from 'react';

import { T, FONT_MONO } from '../constants/tokens';

/**
 * A label/value fact row.
 *
 * Two columns, not stacked: a fixed 120px mono uppercase key on the left and
 * the value on the right, hairline beneath. A column of facts reads as a table
 * this way — the keys line up, so the eye can run down them — which a stacked
 * label/value pair does not give you.
 *
 * `tone` colours the value for the cases the design calls out: a disconnected
 * device or a low battery.
 */
function LineItem({
  value,
  description,
  mono = false,
  tone = null,
  last = false,
  style,
  ...props
}) {
  const isEmpty = value == null || value === '';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px minmax(0, 1fr)',
        gap: 16,
        padding: '12px 0',
        borderBottom: last ? 'none' : '1px solid ' + T.hairlineSoft,
        ...style
      }}
      {...props}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          textTransform: 'uppercase',
          color: T.body
        }}
      >
        {description}
      </span>
      <span
        style={{
          // Serials, versions, counts and timestamps line up when they are
          // mono; names and free text do not want it.
          fontFamily: mono ? FONT_MONO : 'inherit',
          fontSize: 14,
          lineHeight: '20px',
          color: isEmpty ? T.mutedSoft : (tone ?? T.ink),
          overflowWrap: 'anywhere'
        }}
      >
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
}

LineItem.propTypes = {
  description: PropTypes.string,
  style: PropTypes.object,
  mono: PropTypes.bool,
  tone: PropTypes.string,
  last: PropTypes.bool,
  value: PropTypes.any
};

export default LineItem;
